import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../lib/utils";
import { BUTTON_BASE, BUTTON_VARIANTS } from "./button";
import { Icon, type IconComponent } from "./icon";
import { Throbber } from "./throbber";
import { Tooltip, TooltipContent, TooltipTrigger, type TooltipSide } from "./tooltip";

/**
 * v2 IconButton (redesign phase 5, #455): a square, icon-only Button.
 *
 * `label` is required (break #31): it is the accessible name and the tooltip
 * text (break #36). Row actions use this, never a primary Button per row.
 *
 * Tooltip: on by default; `tooltip={false}` when visible text next to the
 * button already says the same. Needs a `TooltipProvider` above it (see
 * tooltip.tsx). The tooltip does not become `aria-describedby`: the label is
 * already the name, so screen readers would read it twice.
 *
 * No tooltip while the button's own menu or popover is open (`aria-expanded`,
 * set by a Radix trigger through `asChild`): the label would sit next to the
 * open menu, and the first Escape would close the tooltip instead of the menu
 * (break #175). Focus coming back on close shows it again, as for any focus.
 *
 * Disabled and loading show no tooltip. Disabled can't be hovered
 * (`pointer-events-none`) or focused; loading stays focusable but shows nothing
 * mid-action. The open state is suppressed rather than the Tooltip unmounted,
 * so a button that starts loading keeps its focus. To explain WHY a button is
 * disabled, wrap it in a `<span>` with its own Tooltip at the call site.
 */

export type IconButtonVariant = "ghost" | "outline" | "default" | "destructive-ghost";
export type IconButtonSize = "xs" | "sm" | "md" | "lg";

const iconButtonVariants = cva(BUTTON_BASE, {
  variants: {
    variant: {
      // Muted at rest so a row of icons stays calm; full ink on hover/press.
      ghost: cn(
        BUTTON_VARIANTS.ghost,
        "text-ink-muted hover:text-ink data-hover:text-ink active:text-ink data-active:text-ink",
      ),
      outline: BUTTON_VARIANTS.outline,
      default: BUTTON_VARIANTS.default,
      // Matrix rule "icon-only = grey icon, red on hover" (break #69): grey like
      // ghost at rest, so a column of Löschen icons doesn't shout; red tint on
      // hover, keyboard focus and press, before the confirm. The text Button's
      // destructive-ghost stays red at rest: its label is the warning.
      "destructive-ghost":
        "bg-transparent text-ink-muted " +
        "hover:bg-destructive-subtle hover:text-destructive-accent data-hover:bg-destructive-subtle data-hover:text-destructive-accent " +
        "focus-visible:bg-destructive-subtle focus-visible:text-destructive-accent data-focus:bg-destructive-subtle data-focus:text-destructive-accent " +
        "active:bg-destructive-subtle active:text-destructive-strong data-active:bg-destructive-subtle data-active:text-destructive-strong",
    } satisfies Record<IconButtonVariant, string>,
    size: {
      // 24: inside inputs and dense table rows · 36: next to an lg Button.
      xs: "size-6",
      sm: "size-7",
      md: "size-8",
      lg: "size-9",
    } satisfies Record<IconButtonSize, string>,
  },
  defaultVariants: { variant: "ghost", size: "md" },
});

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> {
  icon: IconComponent;
  /** Accessible name and tooltip text. German UI text. */
  label: string;
  /** Show `label` as a tooltip. Default true. */
  tooltip?: boolean;
  /** Default top; the nav rail uses right. */
  tooltipSide?: TooltipSide;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** The spinner replaces the icon; `aria-busy`, clicks ignored. */
  loading?: boolean;
  /** Render the single child element (e.g. `<a>`) instead of a `<button>`. */
  asChild?: boolean;
  /** Only with `asChild`: the element to render. */
  children?: React.ReactElement;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon,
    label,
    variant = "ghost",
    size = "md",
    loading = false,
    asChild = false,
    tooltip = true,
    tooltipSide,
    className,
    children,
    onClick,
    type,
    ...props
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);

  // `aria-disabled` too: a link under `asChild` is disabled that way.
  const disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  const expanded = props["aria-expanded"] === true || props["aria-expanded"] === "true";
  const suppressed = loading || disabled || expanded;
  // Opening the menu ends the hover / focus that opened the tooltip; don't bring it back on close.
  React.useEffect(() => {
    if (expanded) setOpen(false);
  }, [expanded]);

  const glyph = loading ? (
    <Throbber decorative size={size === "xs" ? 12 : 16} />
  ) : (
    <Icon icon={icon} size={size === "xs" ? 12 : 16} />
  );

  const shared = {
    className: cn(iconButtonVariants({ variant, size }), className),
    "aria-label": label,
    "aria-busy": loading || undefined,
    onClick: loading ? (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault() : onClick,
  };

  const button =
    asChild && children ? (
      <Slot ref={ref} {...shared} {...props}>
        {glyph}
        <Slottable>{children}</Slottable>
      </Slot>
    ) : (
      <button ref={ref} type={type ?? "button"} {...shared} {...props}>
        {glyph}
      </button>
    );

  if (!tooltip) return button;

  return (
    <Tooltip open={open && !suppressed} onOpenChange={setOpen}>
      {/* Overrides Radix's `aria-describedby` with the caller's own (usually none). */}
      <TooltipTrigger asChild aria-describedby={props["aria-describedby"]}>
        {button}
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </Tooltip>
  );
});

export { IconButton, iconButtonVariants };
